import React, { useState } from 'react';
import { Database, RefreshCw, CheckCircle, AlertCircle, Clock } from 'lucide-react';
import { migrateContactNotesToStructuredFields, MigrationStats } from '../utils/noteMigration';
import { User } from '@supabase/supabase-js';

interface NoteMigrationDashboardProps {
    user: User;
}

export default function NoteMigrationDashboard({ user }: NoteMigrationDashboardProps) {
    const [isMigrating, setIsMigrating] = useState(false);
    const [migrationStats, setMigrationStats] = useState<MigrationStats | null>(null);
    const [migrationComplete, setMigrationComplete] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleMigration = async () => {
        setIsMigrating(true);
        setError(null);
        setMigrationComplete(false);
        setMigrationStats(null);

        try {
            const finalStats = await migrateContactNotesToStructuredFields(
                50, // batch size
                (progressStats) => {
                    setMigrationStats({ ...progressStats });
                }
            );

            setMigrationStats(finalStats);
            setMigrationComplete(true);
            console.log('✅ Migration completed successfully:', finalStats);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
            setError(errorMessage);
            console.error('❌ Migration failed:', err);
        } finally {
            setIsMigrating(false);
        }
    };

    return (
        <div className="bg-gray-800 border border-gray-600 rounded-2xl p-6">
            <div className="flex items-center mb-6">
                <Database className="w-5 h-5 mr-3 text-blue-400" />
                <h3 className="text-lg font-semibold text-white">Notes Migration Tool</h3>
            </div>

            <div className="space-y-4">
                <div className="bg-gray-900 border border-gray-600 rounded-xl p-4">
                    <h4 className="font-medium text-white mb-2">What this does:</h4>
                    <ul className="text-sm text-gray-300 space-y-1">
                        <li>• Parses existing notes into structured database fields</li>
                        <li>• Extracts LinkedIn URLs, TLDR summaries, professional experience, etc.</li>
                        <li>• Makes contact data searchable and better organized</li>
                        <li>• Runs safely without affecting existing notes</li>
                    </ul>
                </div>

                {!migrationComplete && !isMigrating && (
                    <button
                        onClick={handleMigration}
                        disabled={isMigrating}
                        className="w-full px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center space-x-2"
                    >
                        <RefreshCw className="w-4 h-4" />
                        <span>Start Migration</span>
                    </button>
                )}

                {isMigrating && (
                    <div className="bg-blue-900 border border-blue-600 rounded-xl p-4">
                        <div className="flex items-center space-x-2 mb-3">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-400"></div>
                            <span className="text-blue-300 font-medium">Migration in progress...</span>
                        </div>
                        {migrationStats && (
                            <div className="text-sm text-blue-200 space-y-1">
                                <div>Total Contacts: {migrationStats.total}</div>
                                <div>Processed: {migrationStats.processed}</div>
                                <div>Updated: {migrationStats.updated}</div>
                                <div>Skipped: {migrationStats.skipped}</div>
                                {migrationStats.errors > 0 && (
                                    <div className="text-red-300">Errors: {migrationStats.errors}</div>
                                )}
                                <div className="mt-2">
                                    <div className="bg-blue-800 rounded-full h-2">
                                        <div
                                            className="bg-blue-400 h-2 rounded-full transition-all duration-300"
                                            style={{
                                                width: `${migrationStats.total > 0 ? (migrationStats.processed / migrationStats.total) * 100 : 0}%`
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {migrationComplete && migrationStats && (
                    <div className="bg-green-900 border border-green-600 rounded-xl p-4">
                        <div className="flex items-center space-x-2 mb-3">
                            <CheckCircle className="w-5 h-5 text-green-400" />
                            <span className="text-green-300 font-medium">Migration Completed!</span>
                        </div>
                        <div className="text-sm text-green-200 space-y-1">
                            <div>✅ Total Contacts: {migrationStats.total}</div>
                            <div>✅ Successfully Updated: {migrationStats.updated}</div>
                            <div>➖ Skipped (no data to parse): {migrationStats.skipped}</div>
                            {migrationStats.errors > 0 && (
                                <div className="text-red-300">❌ Errors: {migrationStats.errors}</div>
                            )}
                        </div>
                        <button
                            onClick={() => window.location.reload()}
                            className="mt-3 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                        >
                            Refresh Page to View Results
                        </button>
                    </div>
                )}

                {error && (
                    <div className="bg-red-900 border border-red-600 rounded-xl p-4">
                        <div className="flex items-center space-x-2 mb-2">
                            <AlertCircle className="w-5 h-5 text-red-400" />
                            <span className="text-red-300 font-medium">Migration Failed</span>
                        </div>
                        <p className="text-sm text-red-200">{error}</p>
                        <button
                            onClick={() => setError(null)}
                            className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
                        >
                            Try Again
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
